import Reflux from "reflux";
import _ from "lodash";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import CustomerStore from "./CustomerStore";
import StoreListService from "../services/StoreListService";
import FeatureUtil from "../utils/FeatureUtil";
import { sortingCriteriaStoreLists } from "../utils/SortingCriteriaListsUtil";

import { StoreList } from "../datatypes/ShoppingList";
import { GdprStorage } from "../uidispatcherapi/GdprStorage";

const RECENT_STORE_LIST = "recent-store-list";

type State = {
    storelists: Array<StoreList>;
    currentStoreId: string | null | undefined;
    currentIsEmployee: boolean | null | undefined;
    currentIsLoggedIn: boolean | null | undefined;
    currentIsFSDCustomer: boolean | null | undefined;
    currentIsWebCustomer: boolean | null | undefined;
    currentCustomerSegment: string;
    isLoadingStorelists: boolean;
};
const state: State = {
    storelists: [],
    currentStoreId: null,
    currentIsEmployee: null,
    currentIsLoggedIn: null,
    currentIsFSDCustomer: null,
    currentIsWebCustomer: null,
    currentCustomerSegment: "",
    isLoadingStorelists: true
};

const StoreListStore = Reflux.createStore({
    init() {
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this.listenTo(CustomerStore, this.onCustomerStoreTrigger);
        this.onSessionStoreTrigger();
    },

    onSessionStoreTrigger() {
        if (SessionStore.pending() || CustomerStore.isLoading()) {
            return;
        }
        this.loadStorelist();
    },

    onCustomerStoreTrigger() {
        const { currentIsFSDCustomer, currentCustomerSegment } = this.state || {};
        const newCustomerIsFsdCustomer = CustomerStore.isCurrentCustomerFsdCustomer();
        const newCustomerSegment = CustomerStore.getCustomerSegment();
        if (currentIsFSDCustomer !== newCustomerIsFsdCustomer || currentCustomerSegment !== newCustomerSegment) {
            this.loadStorelist();
        }
    },

    loadStorelist() {
        if (SessionStore.getCustomerStore() === null) {
            return;
        }
        const storeId: string | undefined | null = SessionStore.getCustomerStore();
        const storeIdChanged = storeId !== state.currentStoreId;

        const isEmployee = SessionStore.isEmployee();
        const isEmployeeChanged = isEmployee !== state.currentIsEmployee;

        const isLoggedIn = SessionStore.loggedIn();
        const isLoggedInChanged = isLoggedIn !== state.currentIsLoggedIn;

        const isFSDCustomer = CustomerStore.isCurrentCustomerFsdCustomerInSelectedStore();
        const isFSDCustomerChanged = isFSDCustomer !== state.currentIsFSDCustomer;

        const isWebCustomer = CustomerStore.isCurrentCustomerWebCustomer();
        const isWebCustomerChanged = isWebCustomer !== state.currentIsWebCustomer;

        const customerSegment = CustomerStore.getCustomerSegment();
        const isCustomerSegmentChanged = customerSegment !== state.currentCustomerSegment;

        if (
            (storeId && storeIdChanged) ||
            (isEmployee && isEmployeeChanged) ||
            (isLoggedIn && isLoggedInChanged) ||
            (isWebCustomer && isWebCustomerChanged) ||
            (isFSDCustomer && isFSDCustomerChanged) ||
            isCustomerSegmentChanged
        ) {
            state.storelists = [];
            state.isLoadingStorelists = true;
            this.fetchStoreListsForStore(
                storeId,
                isEmployee,
                isLoggedIn,
                isWebCustomer,
                isFSDCustomer,
                customerSegment
            );
            this.trigger();
        }
        if (storeId) {
            state.currentStoreId = storeId;
        }
        if (isEmployee) {
            state.currentIsEmployee = isEmployee;
        }
        if (isLoggedIn) {
            state.currentIsLoggedIn = isLoggedIn;
        }
        if (isWebCustomer) {
            state.currentIsWebCustomer = isWebCustomer;
        }
        if (isFSDCustomer) {
            state.currentIsFSDCustomer = isFSDCustomer;
        }
        state.currentCustomerSegment = customerSegment;
    },

    fetchStoreListsForStore(
        storeId: string,
        isEmployee: boolean,
        isLoggedIn: boolean,
        isWebCustomer: boolean,
        isFSDCustomer: boolean,
        customerSegment: string
    ) {
        const country = SessionStore.getCountry();
        const isWebCustomerEnabled = FeatureUtil.isSLCashAndCarryVisible() && isWebCustomer;

        const self = this;
        StoreListService.getAllStoreListsWithArticles(
            country,
            storeId,
            isEmployee,
            isLoggedIn,
            isWebCustomerEnabled,
            isFSDCustomer,
            customerSegment
        )
            .then(self.setStoreList)
            .catch(self.getAllListsFailed);
    },

    getStoreListData(): State {
        return state;
    },

    getRecentList() {
        const recentStoreListId = GdprStorage.localStorage.get(RECENT_STORE_LIST);
        if (state.storelists) {
            const newestList = _.chain(state.storelists)
                .orderBy(sortingCriteriaStoreLists.sortFn, sortingCriteriaStoreLists.ordering as any)
                .value()[0];

            if (recentStoreListId.actualResult !== "") {
                const recentItem = state.storelists.find(l => l.listId === recentStoreListId.actualResult);
                return recentItem ? recentItem : newestList;
            }

            return newestList;
        }
        return null;
    },

    setStoreList(response: any) {
        state.isLoadingStorelists = false;
        const lists = response.lists;
        state.storelists = _.map(lists, list => {
            list.uniqAmount = list.items.length;
            list.type = "STORELIST";
            list.items = _.map(list.items, article => {
                article.articleVariantId = article.bettyVariantId;
                return article;
            });
            return list;
        });
        this.trigger();
    },

    getAllListsFailed() {
        state.isLoadingStorelists = false;
        state.storelists = [];
        this.trigger();
    },

    // For testing only
    resetStoreId() {
        state.currentStoreId = null;
    }
});

export default StoreListStore;
